
import React, { useContext, useEffect, useState } from 'react'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import Spinner from '../ui-elements/Spinner'
import RedAlert from '../ui-elements/RedAlert'
import { subscribe } from '../utils/pubsub'
import GreenAlert from '../ui-elements/GreenAlert'

const SEARCH_DEBOUNCE_MS = 400



type Props = {
  questionId?: string
  checklistId?: string
  setQuestionAnswer: React.Dispatch<React.SetStateAction<any>>
  defaultValue: null | string | string[]
  handleChangeSelectMultiple: AnyFunction
  thisQuestion: ObjectStringKeyAnyValue
  questionAnswerValue: any
}

function QuestionSelectFromUploadedList({
  questionId,
  checklistId,
  setQuestionAnswer,
  defaultValue,
  handleChangeSelectMultiple,
  thisQuestion,
  questionAnswerValue
}: Props) {

  const {
    t,
    sendMessageToWebsocket
  } = useContext(GlobalContext)

  const questionFormat = thisQuestion.QuestionFormat
  const questionOptionsFromListId = thisQuestion.QuestionOptionsFromListId

  const [listDetails, setListDetails] = useState<null | ObjectStringKeyAnyValue>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [searchText, setSearchText] = useState('')
  const [searchResults, setSearchResults] = useState<null | ObjectStringKeyAnyValue[]>(null)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState<ObjectStringKeyAnyValue>({})


  // clear searchText if the user navigates to a different question using the same component
  useEffect(() => {
    setSearchText('')
  }, [questionId])

  useEffect(() => {
    if (!questionOptionsFromListId) return

    setListDetails(null)
    setErrorMessage(null)

    const requestedId = questionOptionsFromListId

    const unsubscribe = subscribe('fetchOptionListDetailsResponse', (data: ObjectStringKeyAnyValue) => {
      if (data.id !== requestedId) return
      unsubscribe()
      if (data.error) {
        setErrorMessage(data.error)
        return
      }
      setListDetails(data)
    })

    sendMessageToWebsocket(JSON.stringify({
      action: 'uploadedOptionLists',
      subAction: 'fetch_option_list_details',
      Id: `${requestedId}`,
    }))

    return unsubscribe
  }, [questionOptionsFromListId])

  useEffect(() => {
    if (!searchText.trim()) {
      setSearchResults(null)
      setSearchError(null)
      setIsSearching(false)
      return
    }

    let unsubscribe: (() => void) | null = null

    const debounceTimeout = setTimeout(() => {
      setIsSearching(true)
      setSearchError(null)

      unsubscribe = subscribe('searchOptionListResponse', (data: ObjectStringKeyAnyValue) => {
        if (data.id !== questionOptionsFromListId) return
        if (unsubscribe) unsubscribe()
        setIsSearching(false)
        if (data.error) {
          setSearchError(data.error)
          return
        }
        setSearchResults(data.options || [])
      })

      sendMessageToWebsocket(JSON.stringify({
        action: 'uploadedOptionLists',
        subAction: 'search_option_list',
        Id: questionOptionsFromListId,
        searchText: searchText.trim(),
      }))
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(debounceTimeout)
      if (unsubscribe) unsubscribe()
    }
  }, [searchText, questionOptionsFromListId])

  const isOptionSelected = (option: ObjectStringKeyAnyValue) => {
    if (questionFormat === 'SelectMultiple') {
      return Array.isArray(questionAnswerValue) && questionAnswerValue.includes(option.Id)
    }
    return questionAnswerValue === option.Id
  }

  const selectOption = (option: ObjectStringKeyAnyValue) => {
    const optionToAdd = { [option.Id]: option }
    setSelectedOptions(prev => ({ ...prev, ...optionToAdd }))
    if (questionFormat === 'SelectMultiple') {
      handleChangeSelectMultiple(option.Id)
    } else {
      setQuestionAnswer(option.Id)
    }
  }

  const getDisplayTextForSelectedOption = (option: ObjectStringKeyAnyValue) => {
    if (option.displayText === option.Id) return option.displayText
    return `${option.Id} (${option.displayText})`
  }



  return <div className={`w-full flex flex-col gap-2 items-center justify-center`}>

    {!listDetails && !errorMessage && <Spinner />}

    {errorMessage && <RedAlert>{errorMessage}</RedAlert>}

    {listDetails && <>
      <p className={`text-md`}>
        {t('QuestionPages.QuestionSelect.searchForOptionsLabel', { name: listDetails.displayName, count: listDetails.rowCount })}
      </p>

      <div
        className={`w-full bg-white rounded flex flex-row items-center justify-between pr-2`}>
        <input
          type='text'
          value={searchText}
          onChange={(e: any) => setSearchText(e.target.value)}
          placeholder={`${t('QuestionPages.QuestionSelect.searchPlaceholder')}`}
          className={`text-center w-full border pl-6 pr-1 py-2 border-none text-md`}
        />
       <p className={`text-2xl`}>🔎</p>
      </div>

      {isSearching && <Spinner size='small' />}

      {searchError && <RedAlert size='small'>{searchError}</RedAlert>}

      {!isSearching && !searchError && searchResults && searchResults.length < 1 &&
        <RedAlert size='small'>{t('QuestionPages.QuestionSelect.noSearchResults')}</RedAlert>}

      {!isSearching && searchResults && searchResults.length > 0 && <div className={`w-full flex flex-row flex-wrap gap-1 items-center justify-center`}>
        {searchResults.map((option: ObjectStringKeyAnyValue) => {
          return <div
            key={option.Id}
            onClick={() => selectOption(option)}

            className={`
            ${isOptionSelected(option) ? ' hidden ' : ' bg-white text-black '}
            cursor-pointer hover:opacity-80 px-2 py-1 rounded text-xs`}
          >
            {getDisplayTextForSelectedOption(option)}
          </div>
        })}
      </div>}

    </>}


    {questionAnswerValue && Array.isArray(questionAnswerValue) && questionAnswerValue.length > 0 && <div className={`w-full flex flex-row flex-wrap gap-1 items-center justify-center`}>
      {questionAnswerValue.map((optionId: string) => {
        const option = selectedOptions[optionId]
        if (!option) return null
        return <div
          key={optionId}
          onClick={() => selectOption(option)}
          className={`bg-brandblue text-white cursor-pointer hover:opacity-80 rounded px-2 py-1 text-xs`}>
          {getDisplayTextForSelectedOption(option)}
        </div>
      })}
    </div>}

    {questionAnswerValue &&
      typeof questionAnswerValue === 'string' &&
      selectedOptions[questionAnswerValue] &&
      <div className={`w-full flex flex-row flex-wrap gap-1 items-center justify-center`}>
        <div className={`bg-brandblue text-white cursor-pointer hover:opacity-80 rounded px-2 py-1 text-sm font-bold`}>
          {getDisplayTextForSelectedOption(selectedOptions[questionAnswerValue])}
        </div>
      </div>}


  </div>
}
export default QuestionSelectFromUploadedList
